'use client';
import { useMemo } from 'react';

import { DEFAULT_SPORTS_PARAMS } from '@/services/api/sports.api';
import {
  useLiveEvents,
  usePreviousMarkets,
  useSportsGamesInfinite,
  useSportsProps,
} from '@/services/query';
import { EventMarket, EventResponse } from '@/types';
import {
  formatDate as formatDateUtil,
  getMarketType,
  isActuallyLive,
  isEsportsEvent,
} from '@/utils';

import { PAGINATION } from '@/constants';

interface GamesProps {
  tag_id: number | null;
  series_id: number | null;
  effectiveSportSlug: string;
  debouncedSearchQuery: string;
  activeTab: 'games' | 'props';
  gameId?: string;
  showSpreadsAndTotals: boolean;
}

/**
 * Custom hook for fetching and grouping sports games and props.
 */
export const useSportCategoryGames = ({
  tag_id,
  series_id,
  effectiveSportSlug,
  debouncedSearchQuery,
  activeTab,
  gameId,
  showSpreadsAndTotals,
}: GamesProps) => {
  const sportsPropsParams = useMemo(
    () => ({
      tag_id,
      limit: PAGINATION.DEFAULT_LIMIT,
      active: true,
      closed: false,
      live: effectiveSportSlug === 'live' ? true : null,
      search: debouncedSearchQuery || undefined,
    }),
    [tag_id, effectiveSportSlug, debouncedSearchQuery],
  );

  const {
    data: rawPropsEvents = [],
    isLoading: isPropsLoading,
    isFetching: isPropsFetching,
  } = useSportsProps(sportsPropsParams, !!tag_id || !!gameId || effectiveSportSlug === 'live');

  const { data: rawLiveEvents = [] } = useLiveEvents({
    search: debouncedSearchQuery || undefined,
  });

  const { data: previousMarketsRaw = [], isLoading: isPreviousLoading } = usePreviousMarkets({
    series_id: series_id ? String(series_id) : undefined,
    search: debouncedSearchQuery || undefined,
  });

  const gameParams = useMemo(
    () => ({
      // ...(tag_id !== null && tag_id !== undefined ? { tag_id } : { tag_id: 100639 }),
      tag_id: 100639,
      ...DEFAULT_SPORTS_PARAMS,
      order: 'endDate',
      sortOrder: 'asc' as const,
      sport_type: true,
      series_id: series_id || undefined,
      // Stabilize the timestamp to the current minute to prevent infinite refetches
      end_date_min: new Date().toISOString().slice(0, 16) + ':00.000Z',
      search: debouncedSearchQuery || undefined,
    }),
    [tag_id, series_id, debouncedSearchQuery],
  );

  const {
    data: rawGamePages,
    isLoading: isGameLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSportsGamesInfinite(
    gameParams,
    (!!series_id || !!tag_id || effectiveSportSlug === 'live') &&
      (activeTab === 'games' || !!gameId),
  );

  // --- Processing ---

  const rawGameData = useMemo(() => {
    // Merge live events if we are on the live page
    const upcomingGames = (rawGamePages?.pages.flatMap((p) => p.items) ?? []).filter(
      (event) => !!event.homeTeam && !!event.awayTeam && !!event.seriesSlug,
    );

    if (effectiveSportSlug === 'live') {
      // Esports are excluded from this generic all-sports Live tab — they have
      // their own dedicated Esports category (a separate code path, untouched
      // by this branch) where they still show live.
      const liveGames = (rawLiveEvents || []).filter(
        (e) =>
          !!e.homeTeam && !!e.awayTeam && !!e.seriesSlug && !isEsportsEvent(e) && isActuallyLive(e),
      );
      const liveIds = new Set(liveGames.map((g) => g.id));
      const filteredUpcoming = upcomingGames.filter(
        (g) => !liveIds.has(g.id) && !isEsportsEvent(g),
      );
      return [...liveGames, ...filteredUpcoming];
    }

    return upcomingGames;
  }, [rawGamePages, rawLiveEvents, effectiveSportSlug]);

  const apiEvents = useMemo(() => {
    if (!rawGameData) return [];
    return !gameId ? rawGameData : rawGameData.filter((event) => event.id === gameId);
  }, [rawGameData, gameId]);

  const previousMarkets = useMemo(() => {
    return (previousMarketsRaw || []).filter(
      (event) => !!event.homeTeam && !!event.awayTeam && !!event.seriesSlug,
    );
  }, [previousMarketsRaw]);

  const selectedEvent = useMemo(() => {
    if (apiEvents && apiEvents.length > 0) return apiEvents[0];
    if (previousMarkets && previousMarkets.length > 0) return previousMarkets[0];
    return null;
  }, [apiEvents, previousMarkets]);

  const gamesByDate = useMemo(() => {
    const postponedEvents: EventResponse[] = [];
    const normalEvents: EventResponse[] = [];

    apiEvents.forEach((event) => {
      const isPostponed =
        event.period === 'POST' || event.status?.toLowerCase().includes('postponed');
      if (isPostponed) {
        postponedEvents.push(event);
      } else {
        normalEvents.push(event);
      }
    });

    const grouped = normalEvents.reduce(
      (acc, event) => {
        const rawDate = event.gameDate || event.eventDate || event.startDate || event.endDate;
        const dateKey = rawDate
          ? formatDateUtil(rawDate, { weekday: 'short', month: 'long', day: 'numeric' })
          : 'Unknown Date';

        if (!acc[rawDate || 'Unknown Date']) {
          acc[rawDate || 'Unknown Date'] = { label: dateKey, events: [] };
        }
        acc[rawDate || 'Unknown Date'].events.push(event);
        return acc;
      },
      {} as Record<string, { label: string; events: EventResponse[] }>,
    );

    const sortedNormal = Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'Unknown Date') return 1;
        if (b === 'Unknown Date') return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      })
      .reduce(
        (acc, key) => {
          const sortedEvents = [...grouped[key].events].sort((a, b) => {
            const timeA = parseFloat(String(a.startTime ?? 0));
            const timeB = parseFloat(String(b.startTime ?? 0));
            return timeB - timeA;
          });
          acc[grouped[key].label] = sortedEvents;
          return acc;
        },
        {} as Record<string, EventResponse[]>,
      );

    if (postponedEvents.length > 0) {
      sortedNormal['Postponed'] = postponedEvents;
    }

    return sortedNormal;
  }, [apiEvents]);

  const hasMarketType = useMemo(
    () => (type: string) => {
      if (effectiveSportSlug === 'live') return true;
      return apiEvents.some((event) =>
        event.markets?.some((m: EventMarket) => {
          const mType = getMarketType(m);
          return (
            mType === type ||
            (type === 'spread' && mType.includes('spread')) ||
            (type === 'total' && mType.includes('total'))
          );
        }),
      );
    },
    [apiEvents, effectiveSportSlug],
  );

  const columnVisibility = useMemo(() => {
    return {
      hasMoneyline: hasMarketType('moneyline'),
      hasSpread: showSpreadsAndTotals && hasMarketType('spread'),
      hasTotal: showSpreadsAndTotals && hasMarketType('total'),
    };
  }, [hasMarketType, showSpreadsAndTotals]);

  const hasProps = useMemo(() => {
    if (effectiveSportSlug === 'live') return true;
    if (isPropsLoading && tag_id) return true;
    return rawPropsEvents.length > 0;
  }, [effectiveSportSlug, rawPropsEvents, isPropsLoading, tag_id]);

  return {
    apiEvents,
    propsEvents: rawPropsEvents,
    selectedEvent,
    gamesByDate,
    columnVisibility,
    hasProps,
    hasSpreadMarkets: hasMarketType('spread'),
    hasTotalMarkets: hasMarketType('total'),
    isPropsLoading: isPropsLoading || isPropsFetching,
    isGameLoading,
    isPreviousLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
